import React, { useState, useEffect } from 'react';
import { X, Printer, Tags, AlertTriangle } from 'lucide-react';
import { fetchWithAuth, API_URL } from '../utils/api';
import { useLanguage } from './LanguageContext';
import { generateDrugLabelHTML } from '../utils/drugLabelHtml';
import { printViaAgent } from '../utils/printAgent';

// Machine codes the label endpoint returns for the two states that must never
// produce a label. Translated here rather than shown raw so a Thai front desk
// gets a Thai reason; err.message (the server's English text) is the fallback.
const REFUSAL_MESSAGES = {
  NOT_DISPENSED: ['not_dispensed', 'This line has not been dispensed yet'],
  RX_CANCELLED: ['rx_cancelled', 'This prescription was cancelled and cannot be printed'],
};

const DrugLabelPrintModal = ({ isOpen, onClose, prescriptionId, item, currentBranch }) => {
  const { print_lang, currentLanguage, translations } = useLanguage();
  const [settings, setSettings] = useState([]);
  const [selectedSetting, setSelectedSetting] = useState('');
  const [copies, setCopies] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  // A label the generator marked as overflowing, held back for a second,
  // deliberate click. Never printed on the first one.
  const [pending, setPending] = useState(null);
  // Set once a document has actually reached a window. The dialog stays open
  // after a browser fallback so the warning is read, and without this the
  // still-enabled button would send the same label again — the operator is
  // already holding the first one.
  const [sent, setSent] = useState(false);

  const branchId = currentBranch?.branch_id;

  useEffect(() => {
    if (!isOpen || !branchId) return;
    setError(''); setWarning(''); setPending(null); setSent(false);
    fetchWithAuth(`/api/labels/settings?branch_id=${branchId}`)
      .then((res) => {
        const list = res?.data || [];
        setSettings(list);
        const def = list.find((s) => s.is_default) || list[0];
        setSelectedSetting(def ? String(def.setting_id) : '');
      })
      .catch((err) => setError(err.message || 'Failed to load label settings'));
  }, [isOpen, branchId]);

  // Changing the label size or the copy count invalidates the held-back label:
  // a larger label is exactly the fix the warning asks for, so the operator
  // must not be able to change the setting and then click a stale "Print
  // anyway" that prints the label built for the old one.
  useEffect(() => {
    setPending(null);
    setWarning('');
    // Changing a parameter is a deliberate new print intent — a larger label
    // after an overflow warning, or a different copy count — so it re-arms the
    // button that a completed browser print had disabled.
    setSent(false);
  }, [selectedSetting, copies]);

  // Returns false when the popup was blocked, so the caller can keep the dialog
  // open with the reason instead of closing on a print that never happened.
  const printInBrowser = (html) => {
    const w = window.open('', '_blank', 'width=720,height=520');
    if (!w) {
      setError(print_lang('popup_blocked', 'Popup blocked — allow popups and try again'));
      return false;
    }
    w.document.write(html);
    w.document.close();
    return true;
  };

  const send = async ({ agentHtml, browserHtml }) => {
    let popupBlocked = false;
    const result = await printViaAgent(agentHtml, 'label', currentBranch, {
      // The HTML already carries one page per copy (the endpoint echoes the
      // count into the payload and the generator repeats the label), so the
      // agent must not multiply them again.
      copies: 1,
      browserFallback: () => { popupBlocked = !printInBrowser(browserHtml); },
      onWarn: () => setWarning(
        print_lang('printed_via_browser', 'Print agent offline — printed via the browser')
      ),
    });

    // Stay open when we warned, so the message is actually read — and when the
    // popup never opened, so the failure is visible.
    if (!result.warned && !popupBlocked) {
      onClose();
      return;
    }
    if (!popupBlocked) {
      // The document is out: the agent took it, or the popup opened and
      // auto-printed. Retire the held-back label and the button with it, so
      // the dialog that stayed open to show a warning cannot be used to print
      // a duplicate. A blocked popup printed nothing, so that case stays
      // retryable — the operator allows popups and clicks again.
      setPending(null);
      setSent(true);
    }
  };

  const handlePrint = async () => {
    if (!item?.item_id) return;
    setLoading(true); setError(''); setWarning('');
    try {
      const res = await fetchWithAuth(
        `/api/prescriptions/${prescriptionId}/items/${item.item_id}/print-label`,
        { method: 'POST', body: JSON.stringify({ setting_id: selectedSetting || null, copies }) }
      );
      const labelData = res.data;

      // The agent drives its own copies, so the HTML it receives carries the
      // pages but no auto-print script; the browser fallback needs the script.
      const agentHtml = generateDrugLabelHTML(labelData, {
        print_lang, currentLanguage, translations, apiUrl: API_URL, autoPrint: false,
      });
      const browserHtml = generateDrugLabelHTML(labelData, {
        print_lang, currentLanguage, translations, apiUrl: API_URL, autoPrint: true,
      });

      // The generator — not this dialog — decides whether the label fits: it
      // shrinks the font deterministically and reports the verdict on the
      // document's <html> tag, so the settings preview and this print path can
      // never disagree. Read the flag; do not re-measure it here.
      //
      // `overflow: hidden` means an overflowing label prints with content cut
      // off, and no one holding the bag can tell. That is a patient-safety
      // problem, so it is never printed on the first click: the operator is
      // told what is wrong and offered the real fix (a larger label size)
      // alongside an explicit override.
      if (/data-label-overflow="true"/.test(agentHtml)) {
        setPending({ agentHtml, browserHtml });
        return;
      }

      await send({ agentHtml, browserHtml });
    } catch (err) {
      // Both branches prefer the translated string and fall back to the
      // server's message. fetchWithAuth always populates err.message, so
      // testing it first would make the translation unreachable and show a
      // Thai front desk an English error. The raw message is still in the
      // console, where fetchWithAuth logs it, for anyone debugging.
      const refusal = REFUSAL_MESSAGES[err.code];
      setError(refusal
        ? print_lang(refusal[0], err.message || refusal[1])
        : print_lang('print_failed', err.message || 'Print failed'));
    } finally {
      setLoading(false);
    }
  };

  const handlePrintAnyway = async () => {
    if (!pending) return;
    setLoading(true); setError('');
    try {
      await send(pending);
    } catch (err) {
      setError(print_lang('print_failed', err.message || 'Print failed'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-content w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Tags size={20} /> {print_lang('print_drug_label', 'Print Drug Label')}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded mb-4 flex items-center gap-2">
            <AlertTriangle size={16} /> {error}
          </div>
        )}
        {warning && (
          <div className="bg-amber-50 border border-amber-300 text-amber-800 px-3 py-2 rounded mb-4 text-sm">
            {warning}
          </div>
        )}
        {pending && (
          <div className="bg-red-50 border border-red-300 text-red-800 px-3 py-2 rounded mb-4 text-sm">
            <div className="flex items-start gap-2 font-semibold">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <span>
                {print_lang('label_overflow_print_warning',
                  'This label does not fit. Printing it now will cut content off the label.')}
              </span>
            </div>
            <p className="mt-1">
              {print_lang('label_overflow_warning',
                "Content doesn't fit even at the smallest legible size. Choose a larger label or shorten the text.")}
            </p>
          </div>
        )}

        <div className="mb-3 text-sm text-gray-600">
          {item?.medication_name}{item?.strength ? ` ${item.strength}` : ''}
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            {print_lang('label_settings', 'Drug Label Settings')}
          </label>
          <select className="w-full border rounded px-3 py-2" value={selectedSetting}
                  onChange={(e) => setSelectedSetting(e.target.value)}>
            {settings.length === 0 && (
              <option value="">{print_lang('no_label_settings', 'No label settings yet')}</option>
            )}
            {settings.map((s) => (
              <option key={s.setting_id} value={s.setting_id}>
                {s.label_name}{s.is_default ? ' ★' : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">{print_lang('copies', 'Copies')}</label>
          <input type="number" min="1" max="20" className="w-full border rounded px-3 py-2"
                 value={copies} onChange={(e) => setCopies(Number(e.target.value) || 1)} />
        </div>

        <div className="flex justify-end gap-2">
          <button className="btn" onClick={onClose}>{print_lang('cancel', 'Cancel')}</button>
          {pending ? (
            <button className="btn btn-danger flex items-center gap-2"
                    disabled={loading || sent} onClick={handlePrintAnyway}>
              <Printer size={16} />
              {loading ? print_lang('printing', 'Printing...') : print_lang('print_anyway', 'Print anyway')}
            </button>
          ) : (
            <button className="btn btn-primary flex items-center gap-2"
                    disabled={loading || sent} onClick={handlePrint}>
              <Printer size={16} />
              {loading ? print_lang('printing', 'Printing...') : print_lang('print', 'Print')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DrugLabelPrintModal;
